import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import { toast } from "sonner";
import { Send, MessageSquare, ClipboardList, Ban } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [contextPost, setContextPost] = useState(null);
  const [contextRequest, setContextRequest] = useState(null);
  const endRef = useRef(null);

  const loadThreads = () => api.get("/messages/threads").then(r => setThreads(r.data));

  useEffect(() => { if (user) loadThreads(); }, [user]);

  // Deep-link: /messages?to=<userId>&context=<wallPostId> — used by the
  // Wall-linked "Message" CTA on a "Message me" request.
  useEffect(() => {
    if (!user) return;
    const to = searchParams.get("to");
    const context = searchParams.get("context");
    const reqId = searchParams.get("request");
    if (context) {
      api.get(`/wall/post/${context}`).then(r => setContextPost(r.data)).catch(() => {});
    }
    if (reqId) {
      api.get(`/requests/${reqId}`).then(r => setContextRequest(r.data)).catch(() => {});
    }
    if (to) {
      (async () => {
        try {
          const basic = await api.get(`/users/${to}/basic`);
          const thread = await api.get(`/messages/thread/${to}`);
          setActive({ thread_id: [user.id, to].sort().join("_"), other_user: basic.data, last_message: null });
          setMsgs(thread.data);
        } catch { toast.error("Could not open that conversation"); }
      })();
    }
    // eslint-disable-next-line
  }, [user, searchParams]);

  // Realtime: refresh thread on incoming message
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("lima_token");
    if (!token) return;
    const wsBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/api/ws/${user.id}?token=${encodeURIComponent(token)}`);
    ws.onmessage = (e) => {
      try {
        const p = JSON.parse(e.data);
        if (p.type === "message") {
          loadThreads();
          if (active && (p.data.from_id === active.other_user.id || p.data.to_id === active.other_user.id)) {
            setMsgs(prev => [...prev, p.data]);
          }
        }
      } catch {}
    };
    const ping = setInterval(() => { if (ws.readyState === 1) ws.send("ping"); }, 25000);
    return () => { clearInterval(ping); ws.close(); };
    // eslint-disable-next-line
  }, [user?.id, active?.thread_id]);

  useEffect(() => { endRef.current?.scrollIntoView({behavior:"smooth"}); }, [msgs]);

  const openThread = async (t) => {
    setActive(t);
    setContextPost(null);
    setContextRequest(null);
    const r = await api.get(`/messages/thread/${t.other_user.id}`);
    setMsgs(r.data);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      const payload = { to_user_id: active.other_user.id, content: text };
      if (contextPost && msgs.length === 0) payload.context_post_id = contextPost.id;
      if (contextRequest) payload.context_request_id = contextRequest.id;
      await api.post("/messages", payload);
      setText("");
      const r = await api.get(`/messages/thread/${active.other_user.id}`);
      setMsgs(r.data);
      loadThreads();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Could not send"); }
  };

  const blockActive = async () => {
    if (!active || !window.confirm(`Block ${active.other_user.name}? They won't be able to message you.`)) return;
    try {
      await api.post(`/moderation/block/${active.other_user.id}`);
      toast.success("Account blocked");
      setActive(null); setMsgs([]);
      loadThreads();
    } catch { toast.error("Could not block"); }
  };

  if (!user) return <div><Header/><div className="text-center py-20">Sign in to view messages.</div></div>;

  const goToProfile = (u) => navigate(u.role === "pro" ? `/pro/${u.id}` : `/client/${u.id}`);

  return (
    <div>
      <Header/>
      <div className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold mb-6" style={{fontFamily:'Outfit'}}>Messages</h1>
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 grid grid-cols-1 md:grid-cols-3 min-h-[500px] overflow-hidden">
          <div className="border-r border-black/5 overflow-y-auto">
            {threads.length === 0 && (
              <div className="p-8 text-center text-[#222]/50">
                <MessageSquare className="mx-auto mb-3" size={28}/>
                <p className="text-sm">No conversations yet</p>
              </div>
            )}
            {threads.map(t => (
              <button key={t.thread_id} onClick={() => openThread(t)}
                className={`w-full flex items-center gap-3 p-4 border-b border-black/5 text-left hover:bg-black/[0.02] ${active?.thread_id===t.thread_id ? "bg-[#2E6B57]/10" : ""}`}
                data-testid={`thread-${t.other_user.id}`}>
                <div onClick={e => { e.stopPropagation(); goToProfile(t.other_user); }}
                  role="link" title={`View ${t.other_user.name}'s profile`}
                  className="w-10 h-10 rounded-full bg-[#F2F4F2] grid place-items-center font-bold shrink-0 cursor-pointer hover:ring-2 hover:ring-[#2E6B57] transition-shadow overflow-hidden"
                  data-testid={`thread-avatar-${t.other_user.id}`}>
                  {t.other_user.avatar_url ? <img src={t.other_user.avatar_url} className="w-full h-full rounded-full object-cover" alt=""/> : t.other_user.name?.[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{t.other_user.name}</p>
                  <p className="text-xs text-[#222]/50 truncate">{t.last_message?.content}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="md:col-span-2 flex flex-col">
            {active ? (
              <>
                <div className="p-4 border-b border-black/5 flex items-center justify-between">
                  <button onClick={() => goToProfile(active.other_user)} className="flex items-center gap-2.5 group" title={`View ${active.other_user.name}'s profile`} data-testid={`active-avatar-${active.other_user.id}`}>
                    <span className="w-9 h-9 rounded-full bg-[#F2F4F2] grid place-items-center font-bold shrink-0 overflow-hidden group-hover:ring-2 group-hover:ring-[#2E6B57] transition-shadow">
                      {active.other_user.avatar_url ? <img src={active.other_user.avatar_url} className="w-full h-full object-cover" alt=""/> : active.other_user.name?.[0]}
                    </span>
                    <span className="font-semibold group-hover:underline" data-testid="active-thread-name">{active.other_user.name}</span>
                  </button>
                  {user.role === "client" && (
                    <button onClick={blockActive} className="text-xs text-red-500 font-semibold flex items-center gap-1" data-testid="message-block-btn"><Ban size={13}/> Block</button>
                  )}
                </div>
                {contextPost && (
                  <div className="px-4 py-2.5 bg-[#F2F4F2] border-b border-black/5 text-xs flex items-center gap-2 text-[#222]/70" data-testid="message-context-card">
                    <ClipboardList size={13} className="text-[#C79B6B] shrink-0"/>
                    <span className="truncate">Re: Wall request — {contextPost.content}</span>
                  </div>
                )}
                {contextRequest && (
                  <div className="px-4 py-2.5 bg-[#F2F4F2] border-b border-black/5 text-xs flex items-center gap-2 text-[#222]/70" data-testid="message-request-context">
                    <ClipboardList size={13} className="text-[#245544] shrink-0"/>
                    <span className="truncate">Regarding: <span className="font-semibold">{contextRequest.title}</span></span>
                    <Link to={`/requests/${contextRequest.id}`} className="ml-auto text-[#245544] font-semibold shrink-0">View request →</Link>
                  </div>
                )}
                <div className="flex-1 p-4 overflow-y-auto space-y-2 min-h-[300px] max-h-[60vh]">
                  {msgs.map(m => (
                    <div key={m.id} className={`flex ${m.from_id === user.id ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] px-4 py-2 rounded-2xl text-sm ${m.from_id===user.id ? "bg-[#2E6B57] text-[#FFFFFF] rounded-br-sm" : "bg-black/5 rounded-bl-sm"}`}>
                        {m.content}
                      </div>
                    </div>
                  ))}
                  <div ref={endRef}/>
                </div>
                <form onSubmit={send} className="border-t border-black/5 p-3 flex gap-2">
                  <input value={text} onChange={e=>setText(e.target.value)} placeholder="Type a message…"
                    className="flex-1 px-4 py-2 bg-black/5 rounded-full outline-none text-sm"
                    data-testid="message-text-input"/>
                  <button type="submit" className="btn-lime p-2.5" data-testid="message-send-btn"><Send size={16}/></button>
                </form>
              </>
            ) : (
              <div className="flex-1 grid place-items-center text-[#222]/50 text-sm">
                Select a conversation to read messages
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
